/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2024 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { Suspense } from 'react';
import Framework from '@greenville/framework';
import CommonUtils from '../../../common/utils/CommonUtils';
import { MainContainerLoader } from '../../authhome/Loader/ComponentLoader';
import Syllabus from '../models/Syllabus';
import { MCQAssignments } from '../models/MCQAssignments';
import { TocMapping } from '../models/TocMapping';
import Instructor from '../models/Instructor';
import AssignmentModel from '../models/Assignment';
import MCQAssignmentService from '../services/MCQAssignmentService';
import TocMappingService from '../services/PrismTocMappingService';
import BerlinTocMappingService from '../services/BerlinTocMappingService';
import InstructorService from '../services/InstructorService';
import {
  AssignmentCreateService,
  AssignmentGetService,
  AssignmentDeleteService,
  AssignmentUpdateService,
  AssignmentBulkEditService
} from '../services/AssignmentService';
import GetMCQStatusService from '../services/GetMCQStatusService';
import BerlinTitleGenerationService from '../services/BerlinTitleGenerationService';
import AggregateAssignmentMCQService from '../services/AggregateAssignmentMCQService';
import env from '../../../common/env';

import { AET_ROUTES } from '../../../common/constants';
import StudentsRosterModel from '../models/StudentsRoster';
import ChannelRemediationByBook from '../../authhome/model/ChannelRemediationByBook';
import StudentsRosterService from '../services/StudentsRosterService';
import ChangeCourseUserRoleService from '../services/ChangeCourseUserRoleService';
import CourseAssignmentProgressService from '../services/CourseAssignmentProgressService';
import AssignmentAllStudentsProgressService from '../services/AssignmentAllStudentsProgressService';
import MCQStatusService from '../services/MCQStatusService';
import StudentsModel from '../models/Students';
import ChannelRemediationByBookService from '../../authhome/service/ChannelRemediationByBookService';
import ProductMappingService from '../services/ProductMappingService';
import StudentAssignmentDueDatesPostService from '../services/StudentAssignmentDueDatesService';
import StruggleStudentsService from '../services/StruggleStudentsService';
import DifficultQuestionsService from '../services/DifficultQuestionsService';
import QuestionStatisticsService from '../services/QuestionStatisticsService';
import AetMcqFeedbackService from '../services/AetMcqFeedbackService';

const InstructorDashboardContainer = CommonUtils.lazyWithPreload(
  () => import('../../mlmhome/InstructorDashboardContainer')
);

Framework.getClientManager().add({
  name: 'berlin',
  authHeader: 'authorization',
  timeout: env.BERLIN_URL_TIMEOUT,
  baseUrl: env.BERLIN.STREAM_BASE_URL,
  tokenPrefix: 'Bearer '
});


export default {
  path: [
    AET_ROUTES.HOME,
    AET_ROUTES.SYLLABUS,
    AET_ROUTES.SUCCESS,
    AET_ROUTES.STUDENT_ROSTER,
    AET_ROUTES.STUDENT_ROSTER_DETAILS,
    AET_ROUTES.ASSIGNMENTS,
    AET_ROUTES.ASSIGNMENT_DETAILS,
    AET_ROUTES.STUDENT_DETAILS,
    AET_ROUTES.STUDENT_REVIEW,
    AET_ROUTES.DIFFICULT_TOPIC_DETAILS
  ],

  exact: true,
  services: [
    new MCQAssignmentService(),
    new TocMappingService(),
    new BerlinTocMappingService(),
    new AssignmentCreateService(),
    new AssignmentGetService(),
    new AssignmentDeleteService(),
    new GetMCQStatusService(),
    new InstructorService(),
    new StudentsRosterService(),
    new ChangeCourseUserRoleService(),
    new CourseAssignmentProgressService(),
    new AssignmentAllStudentsProgressService(),
    new AssignmentUpdateService(),
    new BerlinTitleGenerationService(),
    new AssignmentBulkEditService(),
    new MCQStatusService(),
    new ChannelRemediationByBookService(),
    new ProductMappingService(),
    new AggregateAssignmentMCQService(),
    new StudentAssignmentDueDatesPostService(),
    new StruggleStudentsService(),
    new DifficultQuestionsService(),
    new QuestionStatisticsService(),
    new AetMcqFeedbackService()
  ],
  stores: [{
    name: 'syllabus',
    spec: Syllabus,
    saveLocally: false,
    initialState: {
      url: '',
      fileId: ''
    }
  },
  {
    name: 'mcqAssignments',
    spec: MCQAssignments,
    saveLocally: false,
    initialState: {
      bookMcqsRequestId: '',
      bookMcqsResponseId: '',
      mcqQuestions: []
    }
  }, {
    name: 'prismTocMapping',
    spec: TocMapping,
    saveLocally: false,
    initialState: {
      chapters: []
    }
  }, {
    name: 'assignment',
    spec: AssignmentModel,
    saveLocally: false,
    initialState: {
      assignments: []
    }
  }, {
    name: 'instructor',
    spec: Instructor,
    saveLocally: false,
    initialState: {
      courseId: '',
      courseTitle: '',
      courseSchedule: [],
      instructors: []
    }
  }, {
    name: 'studentsRoster',
    spec: StudentsRosterModel,
    saveLocally: false,
    initialState: {
      students: []
    }
  }, {
    name: 'students',
    spec: StudentsModel,
    saveLocally: false,
    initialState: {
      students: [],
      allUsers: []
    }
  }, {
    name: 'channelRemediationByBook',
    spec: ChannelRemediationByBook,
    saveLocally: false,
    initialState: {
      channelData: null
    }
  }],
  render: props => (
    <Suspense fallback={<MainContainerLoader />}>
      <InstructorDashboardContainer {...props} />
    </Suspense>
  )
};
